/**
 * Evidence Card Component
 * Displays individual evidence items with type-specific formatting
 */

import {
  GitCommit,
  Code,
  TestTube,
  CheckSquare,
  GitMerge,
  FileCheck,
  AlertCircle,
  CheckCircle,
  Clock,
  XCircle,
} from 'lucide-react';
import type { Evidence, ValidationStatus } from '../../../api/evidence';

interface EvidenceCardProps {
  evidence: Evidence;
  showDetails?: boolean;
}

const TYPE_CONFIG: Record<string, { icon: typeof GitCommit; label: string; color: string }> = {
  commits: { icon: GitCommit, label: 'Commits', color: '#8B5CF6' },
  code_stats: { icon: Code, label: 'Code Stats', color: '#3B82F6' },
  test_results: { icon: TestTube, label: 'Test Results', color: '#10B981' },
  review_approval: { icon: CheckSquare, label: 'Review Approval', color: '#F59E0B' },
  merge_details: { icon: GitMerge, label: 'Merge Details', color: '#EC4899' },
  issue_closed: { icon: FileCheck, label: 'Issue Closed', color: '#6B7280' },
};

const STATUS_CONFIG: Record<ValidationStatus, { icon: typeof CheckCircle; color: string; label: string }> = {
  valid: { icon: CheckCircle, color: '#22C55E', label: 'Valid' },
  invalid: { icon: XCircle, color: '#EF4444', label: 'Invalid' },
  pending: { icon: Clock, color: '#F59E0B', label: 'Pending' },
  error: { icon: AlertCircle, color: '#EF4444', label: 'Error' },
};

export function EvidenceCard({ evidence, showDetails = false }: EvidenceCardProps) {
  const typeConfig = TYPE_CONFIG[evidence.evidenceType] || {
    icon: AlertCircle,
    label: evidence.evidenceType,
    color: '#6B7280',
  };
  const TypeIcon = typeConfig.icon;

  const statusConfig = STATUS_CONFIG[evidence.validationStatus || 'pending'];
  const StatusIcon = statusConfig.icon;

  const timestamp = new Date(evidence.timestamp);

  return (
    <div
      className="p-4 rounded-lg border bg-white hover:shadow-md transition-all"
      style={{ borderColor: '#E8E0D5' }}
    >
      <div className="flex items-start gap-3">
        {/* Type Icon */}
        <div
          className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: typeConfig.color + '20', color: typeConfig.color }}
        >
          <TypeIcon className="w-5 h-5" />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <h4 className="font-medium truncate" style={{ color: '#2F241B' }}>
              {typeConfig.label}
            </h4>
            <div className="flex items-center gap-2 flex-shrink-0">
              {/* Quality Score */}
              {evidence.qualityScore !== undefined && (
                <span
                  className="text-xs font-medium px-2 py-0.5 rounded"
                  style={{
                    backgroundColor:
                      evidence.qualityScore >= 70 ? '#DCFCE7' : evidence.qualityScore >= 40 ? '#FEF3C7' : '#FEE2E2',
                    color:
                      evidence.qualityScore >= 70 ? '#166534' : evidence.qualityScore >= 40 ? '#92400E' : '#991B1B',
                  }}
                >
                  {Math.round(evidence.qualityScore)}%
                </span>
              )}
              {/* Status Badge */}
              <div
                className="flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded"
                style={{ backgroundColor: statusConfig.color + '20', color: statusConfig.color }}
              >
                <StatusIcon className="w-3 h-3" />
                <span>{statusConfig.label}</span>
              </div>
            </div>
          </div>

          {/* Timestamp and Stage */}
          <div className="flex items-center gap-2 mt-1 text-xs" style={{ color: '#A39686' }}>
            <span>{evidence.stage}</span>
            <span>•</span>
            <span>{timestamp.toLocaleDateString()}</span>
            <span>{timestamp.toLocaleTimeString()}</span>
          </div>

          {/* Type-specific details */}
          {showDetails && evidence.evidenceData && (
            <div className="mt-3">
              <EvidenceDetails evidence={evidence} />
            </div>
          )}

          {/* Validation errors */}
          {evidence.validationErrors && evidence.validationErrors.length > 0 && (
            <div className="mt-2 p-2 rounded text-xs" style={{ backgroundColor: '#FEE2E2', color: '#991B1B' }}>
              {evidence.validationErrors.map((error, idx) => (
                <p key={idx}>{error}</p>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Type-specific evidence details
 */
function EvidenceDetails({ evidence }: { evidence: Evidence }) {
  const data = evidence.evidenceData;
  if (!data) return null;

  switch (evidence.evidenceType) {
    case 'commits':
      return <CommitDetails data={data} />;
    case 'code_stats':
      return <CodeStatsDetails data={data} />;
    case 'test_results':
      return <TestResultsDetails data={data} />;
    case 'review_approval':
      return <ReviewDetails data={data} />;
    case 'merge_details':
      return <MergeDetails data={data} />;
    case 'bdd_verification':
    case 'test_plan_verification':
      return <BddVerificationDetails data={data} />;
    default:
      return <GenericDetails data={data} />;
  }
}

function CommitDetails({ data }: { data: Record<string, unknown> }) {
  const commits = (data.commits || data.commit_shas || []) as Array<{
    sha?: string;
    message?: string;
    author?: string;
    filesChanged?: number;
  }>;

  if (commits.length === 0) {
    return (
      <p className="text-sm" style={{ color: '#6B5D52' }}>
        {data.commit_count || 0} commits
      </p>
    );
  }

  return (
    <div className="space-y-2">
      {commits.slice(0, 3).map((commit, idx) => (
        <div key={idx} className="flex items-start gap-2 text-sm">
          <code className="text-xs px-1 py-0.5 rounded" style={{ backgroundColor: '#F5F1EC', color: '#6B5D52' }}>
            {typeof commit === 'string' ? commit.slice(0, 7) : commit.sha?.slice(0, 7)}
          </code>
          {typeof commit !== 'string' && commit.message && (
            <span className="truncate" style={{ color: '#2F241B' }}>
              {commit.message}
            </span>
          )}
        </div>
      ))}
      {commits.length > 3 && (
        <p className="text-xs" style={{ color: '#A39686' }}>
          +{commits.length - 3} more commits
        </p>
      )}
    </div>
  );
}

function CodeStatsDetails({ data }: { data: Record<string, unknown> }) {
  const stats = data as { linesAdded?: number; linesDeleted?: number; filesModified?: number };

  return (
    <div className="flex items-center gap-4 text-sm">
      {stats.linesAdded !== undefined && (
        <span style={{ color: '#22C55E' }}>+{stats.linesAdded}</span>
      )}
      {stats.linesDeleted !== undefined && (
        <span style={{ color: '#EF4444' }}>-{stats.linesDeleted}</span>
      )}
      {stats.filesModified !== undefined && (
        <span style={{ color: '#6B5D52' }}>{stats.filesModified} files</span>
      )}
    </div>
  );
}

function TestResultsDetails({ data }: { data: Record<string, unknown> }) {
  const results = data as { passed?: number; failed?: number; skipped?: number; coverage?: number };

  return (
    <div className="flex items-center gap-4 text-sm">
      {results.passed !== undefined && (
        <span style={{ color: '#22C55E' }}>{results.passed} passed</span>
      )}
      {results.failed !== undefined && results.failed > 0 && (
        <span style={{ color: '#EF4444' }}>{results.failed} failed</span>
      )}
      {results.skipped !== undefined && results.skipped > 0 && (
        <span style={{ color: '#F59E0B' }}>{results.skipped} skipped</span>
      )}
      {results.coverage !== undefined && (
        <span style={{ color: '#6B5D52' }}>{results.coverage}% coverage</span>
      )}
    </div>
  );
}

function ReviewDetails({ data }: { data: Record<string, unknown> }) {
  const review = data as { reviewer?: string; approved?: boolean; comments?: string };

  return (
    <div className="text-sm" style={{ color: '#6B5D52' }}>
      {review.reviewer && <p>Reviewed by: {review.reviewer}</p>}
      {review.approved !== undefined && (
        <p style={{ color: review.approved ? '#22C55E' : '#EF4444' }}>
          {review.approved ? 'Approved' : 'Changes Requested'}
        </p>
      )}
      {review.comments && <p className="mt-1 italic">{review.comments}</p>}
    </div>
  );
}

function MergeDetails({ data }: { data: Record<string, unknown> }) {
  const merge = data as { mergeSha?: string; conflictsResolved?: number; deploymentStatus?: string };

  return (
    <div className="text-sm" style={{ color: '#6B5D52' }}>
      {merge.mergeSha && (
        <p>
          Merge SHA:{' '}
          <code className="text-xs px-1 py-0.5 rounded" style={{ backgroundColor: '#F5F1EC' }}>
            {merge.mergeSha.slice(0, 7)}
          </code>
        </p>
      )}
      {merge.conflictsResolved !== undefined && merge.conflictsResolved > 0 && (
        <p>{merge.conflictsResolved} conflicts resolved</p>
      )}
      {merge.deploymentStatus && <p>Deployment: {merge.deploymentStatus}</p>}
    </div>
  );
}

function BddVerificationDetails({ data }: { data: Record<string, unknown> }) {
  const screenshotUrl = data.screenshotUrl as string | undefined;
  const scenarioName = data.scenarioName as string | undefined;
  const mappedTest = data.mappedTest as string | undefined;
  const mappingConfidence = data.mappingConfidence as number | undefined;

  // Build the full URL — screenshot paths are relative to the API origin
  const imgSrc = screenshotUrl
    ? screenshotUrl.startsWith('http')
      ? screenshotUrl
      : `${window.location.origin}${screenshotUrl}`
    : null;

  return (
    <div className="space-y-2">
      {scenarioName && (
        <p className="text-sm font-medium truncate" style={{ color: '#2F241B' }}>{scenarioName}</p>
      )}
      {mappedTest && (
        <p className="text-xs truncate" style={{ color: '#6B5D52' }}>
          Test: <span className="font-mono">{mappedTest}</span>
          {mappingConfidence !== undefined && (
            <span className="ml-1" style={{ color: '#A39686' }}>({Math.round(mappingConfidence * 100)}% confidence)</span>
          )}
        </p>
      )}
      {imgSrc && (
        <a href={imgSrc} target="_blank" rel="noopener noreferrer" className="block mt-2">
          <img
            src={imgSrc}
            alt={scenarioName || 'Test screenshot'}
            className="rounded border max-h-48 w-full object-contain cursor-zoom-in"
            style={{ borderColor: '#E8E0D5', backgroundColor: '#F5F1EC' }}
          />
          <p className="text-xs mt-1" style={{ color: '#A39686' }}>Click to open full size</p>
        </a>
      )}
    </div>
  );
}

function GenericDetails({ data }: { data: Record<string, unknown> }) {
  const entries = Object.entries(data).slice(0, 5);
  if (entries.length === 0) return null;

  return (
    <div className="text-xs space-y-1" style={{ color: '#6B5D52' }}>
      {entries.map(([key, value]) => (
        <p key={key}>
          <span className="font-medium">{key}:</span>{' '}
          {typeof value === 'object' ? JSON.stringify(value) : String(value)}
        </p>
      ))}
    </div>
  );
}

export default EvidenceCard;
